import { useState, useRef, useEffect } from 'react';
import { Sensitive } from '../components/Sensitive';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Shield, Calendar, Key, Upload, MapPin, Plus, X, Clock, TrendingUp, Zap, Phone, Receipt } from 'lucide-react';
import { PaymentHistory } from '../components/payments/PaymentHistory';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function Profile() {
  const { user, getAuthHeader } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updating, setUpdating] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [extendedProfile, setExtendedProfile] = useState(null);
  const [licensedStates, setLicensedStates] = useState([]);
  const [newState, setNewState] = useState('');
  const [newLicenseNumber, setNewLicenseNumber] = useState('');
  const [savingStates, setSavingStates] = useState(false);
  const [phone, setPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  useEffect(() => { 
    fetchExtendedProfile(); 
  }, []);

  const fetchExtendedProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile-extended`, getAuthHeader());
      setExtendedProfile(response.data);
      setLicensedStates(response.data.licensed_states || []);
      setPhone(user?.phone || response.data.phone || '');
    } catch (error) {
      console.error('Failed to fetch extended profile:', error);
    }
  };

  const handleAddState = () => {
    if (!newState) { toast.error('Please select a state'); return; }
    if (licensedStates.some(s => s.state === newState)) { toast.error('State already added'); return; }
    setLicensedStates([...licensedStates, { state: newState, license_number: newLicenseNumber || null }]);
    setNewState(''); setNewLicenseNumber('');
    toast.success(`${newState} added! Click Save to persist.`);
  };

  const handleRemoveState = (stateToRemove) => {
    setLicensedStates(licensedStates.filter(s => s.state !== stateToRemove));
  };

  const handleSaveLicensedStates = async () => {
    setSavingStates(true);
    try {
      await axios.put(`${API}/user/licensed-states`, { licensed_states: licensedStates }, getAuthHeader());
      toast.success('Licensed states saved!');
      fetchExtendedProfile();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingStates(false);
    }
  };

  const handleSavePhone = async () => {
    setSavingPhone(true);
    try {
      await axios.put(`${API}/user/phone`, { phone: phone }, getAuthHeader());
      toast.success('Phone number saved!');
      fetchExtendedProfile();
    } catch (error) {
      toast.error('Failed to save phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setUpdating(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { new_password: newPassword }, getAuthHeader());
      toast.success('Password updated!');
      setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }
    // Downscale client-side so we never upload multi-MB base64 payloads
    const img = new window.Image();
    img.onload = () => {
      const MAX = 800;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      uploadProfilePicture(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); toast.error('Could not read image'); };
    img.src = URL.createObjectURL(file);
  };

  const uploadProfilePicture = async (base64Image) => {
    setUploading(true);
    try {
      const response = await axios.post(`${API}/auth/profile-picture`, { profile_picture: base64Image }, getAuthHeader());
      setProfilePicture(response.data.profile_picture);
      toast.success('Profile picture updated!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="profile-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-3">
        
        <div>
          <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2B3674]">Profile</h1>
          <p className="text-xs text-[#A3AED0]">Manage your account settings</p>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left Column - Photo & Account Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Profile Photo & Basic Info */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  {profilePicture ? (
                    <img src={profilePicture} alt="Profile" className="w-20 h-20 rounded-xl object-cover border-2 border-[#2447D6]/20" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#2447D6] to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                      {user?.name?.charAt(0)}
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="absolute -bottom-1 -right-1 p-1.5 rounded-lg bg-white border border-[#E9EDF7] hover:bg-[#F4F7FE] transition-colors"
                  >
                    <Upload className="h-3 w-3 text-[#2B3674]/80" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-[#2B3674] truncate">{user?.name}</h2>
                  <p className="text-sm text-[#A3AED0]">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3FBF] capitalize">{user?.role}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"><Sensitive>{user?.comp_percentage}%</Sensitive> comp</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Details Grid */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#2B3674] mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-[#2447D6]" /> Account Details
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <InfoItem icon={Mail} label="Email" value={user?.email} />
                <InfoItem icon={Shield} label="Role" value={user?.role} capitalize />
                <InfoItem icon={Zap} label="Commission" value={<Sensitive>{`${user?.comp_percentage}%`}</Sensitive>} />
                {user?.npn && <InfoItem label="NPN" value={user?.npn} mono />}
                {user?.upline_name && <InfoItem icon={User} label="Upline" value={user?.upline_name} />}
                <InfoItem icon={Calendar} label="Member Since" value={new Date(user?.created_at).toLocaleDateString()} />
                {extendedProfile?.last_login && <InfoItem icon={Clock} label="Last Login" value={new Date(extendedProfile.last_login).toLocaleDateString()} />}
                {extendedProfile?.last_production_date && <InfoItem icon={TrendingUp} label="Last Production" value={new Date(extendedProfile.last_production_date).toLocaleDateString()} />}
              </div>
            </CardContent>
          </Card>

          {/* Phone Number */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#2B3674] mb-3 flex items-center gap-2">
                <Phone className="h-4 w-4 text-[#2447D6]" /> Contact Phone Number
              </h3>
              <p className="text-xs text-[#A3AED0] mb-3">
                This phone number will be visible to your clients in their portal
              </p>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1"
                  data-testid="phone-input"
                />
                <Button
                  onClick={handleSavePhone}
                  disabled={savingPhone}
                  size="sm"
                  className="bg-gradient-to-r from-[#2447D6] to-blue-500"
                  data-testid="save-phone-btn"
                >
                  {savingPhone ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Licensed States */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#2B3674] mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#2447D6]" /> Licensed States
                {licensedStates.length > 0 && <span className="ml-auto text-[10px] bg-blue-100 text-[#1E3FBF] px-1.5 py-0.5 rounded-full">{licensedStates.length}</span>}
              </h3>
              
              {licensedStates.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {licensedStates.map((ls) => (
                    <div key={ls.state} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#F4F7FE] border border-[#E9EDF7]">
                      <span className="text-sm font-medium text-[#2B3674]">{ls.state}</span>
                      {ls.license_number && <span className="text-xs text-[#A3AED0] font-mono">{ls.license_number}</span>}
                      <button onClick={() => handleRemoveState(ls.state)} className="p-0.5 hover:bg-red-100 rounded">
                        <X className="h-3 w-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <select
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  className="flex-1 h-9 rounded-lg border border-[#E9EDF7] bg-[#F4F7FE] px-3 text-sm"
                >
                  <option value="">Select state</option>
                  {US_STATES.filter(s => !licensedStates.some(ls => ls.state === s)).map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                <Input
                  placeholder="License # (opt)"
                  value={newLicenseNumber}
                  onChange={(e) => setNewLicenseNumber(e.target.value)}
                  className="w-32 h-9"
                />
                <Button size="sm" variant="outline" onClick={handleAddState} className="h-9 px-3">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <Button onClick={handleSaveLicensedStates} disabled={savingStates} size="sm" className="mt-3 bg-gradient-to-r from-[#2447D6] to-blue-500">
                {savingStates ? 'Saving...' : 'Save States'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Password */}
        <div className="space-y-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#2B3674] mb-3 flex items-center gap-2">
                <Key className="h-4 w-4 text-[#2447D6]" /> Change Password
              </h3>
              <form onSubmit={handlePasswordReset} className="space-y-3" data-testid="password-reset-form">
                <div className="space-y-1">
                  <Label className="text-xs">New Password</Label>
                  <Input
                    type="password"
                    placeholder="Min 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-9"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Confirm Password</Label>
                  <Input
                    type="password"
                    placeholder="Re-enter"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-9"
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button type="submit" disabled={updating} size="sm" className="w-full" data-testid="update-password-button">
                  {updating ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="bg-white" data-testid="profile-payments-card">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-[#2447D6]" />
            <h3 className="text-sm font-semibold text-[#2B3674]">Payments & Receipts</h3>
          </div>
          <PaymentHistory />
        </CardContent>
      </Card>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, mono, capitalize }) {
  return (
    <div className="py-2 px-3 rounded-lg bg-[#F4F7FE]">
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="h-3 w-3 text-[#A3AED0]" />}
        <span className="text-[10px] text-[#A3AED0] uppercase tracking-wide">{label}</span>
      </div>
      <span className={`text-sm text-[#2B3674] ${mono ? 'font-mono' : ''} ${capitalize ? 'capitalize' : ''}`}>{value}</span>
    </div>
  );
}
